import React, { useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminPagination, {
  AdminSearchRow,
} from '../../components/admin/AdminPagination';
import { ConfirmModal } from '../../components/dicionarios';
import useAdminList from '../../hooks/useAdminList';
import { revalidateCadastroOperacional } from '../../hooks/clearCache';
import * as adminConvenios from '../../services/adminConvenios';
import handleFieldChange from '../../components/admin/formHelpers';

const emptyForm = {
  codConv: '',
  siglaConv: '',
  descriConv: '',
  regAns: '',
  tipo: 'I',
  online: '',
  pesqConv: '',
};

export default function Convenios() {
  const list = useAdminList('admin-convenios', adminConvenios.listConvenios);
  const [formData, setFormData] = useState(emptyForm);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectItem = index => {
    const item = list.items[index];
    if (!item) return;
    setSelectedIndex(index);
    setFormData({
      codConv: item.codConv != null ? String(item.codConv) : '',
      siglaConv: item.siglaConv ?? '',
      descriConv: item.descriConv ?? '',
      regAns: item.regAns ?? '',
      tipo: item.tipo ?? 'I',
      online: item.online ?? '',
      pesqConv: item.pesqConv ?? '',
    });
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleNew = () => {
    setFormData({ ...emptyForm });
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleEdit = () => {
    if (!list.items.length) {
      toast.warn('Nenhum registro para editar!');
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    if (list.items.length) selectItem(Math.min(selectedIndex, list.items.length - 1));
    else setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (!formData.siglaConv?.trim() || !formData.descriConv?.trim()) {
      toast.warn('Sigla e descrição são obrigatórias!');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        siglaConv: formData.siglaConv.trim(),
        descriConv: formData.descriConv.trim(),
        regAns: formData.regAns || null,
        tipo: formData.tipo || 'I',
        online: formData.online || null,
        pesqConv: formData.pesqConv || null,
      };
      if (isCreating) {
        if (formData.codConv) payload.codConv = Number(formData.codConv);
        await adminConvenios.createConvenio(payload);
        toast.success('Convênio criado!');
      } else {
        await adminConvenios.updateConvenio(Number(formData.codConv), payload);
        toast.success('Convênio atualizado!');
      }
      setIsCreating(false);
      setIsEditing(false);
      await list.mutate();
      revalidateCadastroOperacional();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar convênio!');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminConvenios.deleteConvenio(Number(formData.codConv));
      toast.success('Convênio excluído!');
      setShowDeleteConfirm(false);
      await list.mutate();
      revalidateCadastroOperacional();
      setFormData(emptyForm);
      setSelectedIndex(0);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao excluir convênio!');
    }
  };

  return (
    <AdminPageShell
      title="Cadastro de Convênios"
      subtitle="Gestão de convênios (ARQ_CONVENIOS)"
    >
      <AdminSearchRow
        q={list.q}
        onChange={list.setQ}
        placeholder="Buscar por sigla, descrição ou código"
      />

      {list.isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Sigla</th>
                  <th>Descrição</th>
                  <th>Reg. ANS</th>
                  <th>Tipo</th>
                  <th>Online</th>
                  <th>Pesq.</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item, index) => (
                  <tr
                    key={item.codConv}
                    onClick={() => selectItem(index)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        index === selectedIndex && !isCreating
                          ? '#e2f0f0'
                          : undefined,
                    }}
                  >
                    <td>{item.codConv}</td>
                    <td>{item.siglaConv}</td>
                    <td>{item.descriConv}</td>
                    <td>{item.regAns}</td>
                    <td>{item.tipo}</td>
                    <td>{item.online}</td>
                    <td>{item.pesqConv}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {!list.items.length && (
            <Alert variant="light" className="text-center">
              Nenhum convênio encontrado.
            </Alert>
          )}
          <AdminPagination
            page={list.page}
            total={list.total}
            limit={list.limit}
            onPageChange={list.setPage}
          />
        </>
      )}

      <hr />
      <Form>
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Código</Form.Label>
              <Form.Control
                value={formData.codConv ?? ''}
                disabled={!isCreating}
                onChange={handleFieldChange(setFormData, 'codConv')}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Sigla</Form.Label>
              <Form.Control
                value={formData.siglaConv ?? ''}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'siglaConv')}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                value={formData.descriConv ?? ''}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'descriConv')}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Registro ANS</Form.Label>
              <Form.Control
                value={formData.regAns ?? ''}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'regAns')}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.tipo ?? ''}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'tipo')}
                aria-label="Tipo do convênio"
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Online</Form.Label>
              <Form.Control
                maxLength={5}
                value={formData.online ?? ''}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'online')}
                aria-label="Online"
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Pesq.</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.pesqConv ?? ''}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'pesqConv')}
                aria-label="Pesquisa convênio"
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <div className="d-flex flex-wrap gap-2 mt-3">
        <Button variant="primary" className="mr-2 mb-2" onClick={handleNew} disabled={isEditing}>
          Novo
        </Button>
        <Button
          variant="secondary"
          className="mr-2 mb-2"
          onClick={handleEdit}
          disabled={isEditing || !list.items.length}
        >
          Editar
        </Button>
        <Button
          variant="success"
          className="mr-2 mb-2"
          onClick={handleSave}
          disabled={!isEditing || isSaving}
        >
          {isSaving ? 'Gravando...' : 'Gravar'}
        </Button>
        <Button variant="warning" className="mr-2 mb-2" onClick={handleCancel} disabled={!isEditing}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          className="mr-2 mb-2" 
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isCreating || !formData.codConv || isEditing}
        >
          Excluir
        </Button>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Excluir convênio"
        message="Confirma a exclusão deste convênio?"
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />
    </AdminPageShell>
  );
}
